import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type LegalEntityFormSchema } from '@webapp/platform/legal-entities/components/LegalEntityForm'
import { type LegalEntity } from '@webapp/platform/legal-entities/entities/LegalEntity'
import { useLegalEntityUniquenessChecks } from '@webapp/platform/legal-entities/hooks/useLegalEntityUniquenessChecks'
import { mapLegalEntityToFormValues } from './mappers'
import messages from './messages'
import { useMutateUpdateLegalEntity } from '../../models/useMutateUpdateLegalEntity'

interface UseEditLegalEntityFormPresenterParams {
  entity: LegalEntity
  onSuccess?: () => void
}

export const useEditLegalEntityFormPresenter = ({
  entity,
  onSuccess,
}: UseEditLegalEntityFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync } = useMutateUpdateLegalEntity()

  // Pass the edited entity so its own name/external ID is allowed (not flagged
  // as a duplicate when left unchanged).
  const { isNameTaken, isExternalIdTaken } =
    useLegalEntityUniquenessChecks(entity)

  const initialValues = mapLegalEntityToFormValues(entity)

  const handleSave = async (formValues: LegalEntityFormSchema) => {
    if (!formValues.baseCurrency) {
      return
    }

    try {
      await mutateAsync({
        uuid: entity.uuid,
        input: {
          name: formValues.name,
          baseCurrencyId: formValues.baseCurrency.id,
          country: formValues.country?.name,
          externalId: formValues.externalId,
        },
      })

      onSuccess?.()
    } catch (err) {
      // The presenter owns the error toast (ADR-10); the hosting Feature Set
      // handles success (toast + close) via onSuccess.
      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children: getMessageFromError(err),
      })
    }
  }

  return {
    state: {
      initialValues,
    },
    methods: {
      handleSave,
      isNameTaken,
      isExternalIdTaken,
    },
  }
}
